<script setup lang="ts">
import { computed, nextTick, onMounted, ref, onBeforeUnmount } from 'vue';
import Plyr from 'plyr';
import { Video } from '~/types';

interface Props {
  video: Video;
}

const props = defineProps<Props>();

const youtubeVideoContainer = ref<HTMLDivElement>();
const player = ref<Plyr | null>(null);

const setupPlayer = (): void => {
  if (youtubeVideoContainer.value && youtubeEmbedId) {
    player.value = new Plyr(youtubeVideoContainer.value, props.videoOptions);
  }
};

const youtubeEmbedId = computed<string | null>(() => {
  if (!props.video.youtube_url) {
    return null;
  }

  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = props.video.youtube_url.match(regExp);

  return match && match[7].length == 11 ? match[7] : null;
});

onMounted(() => {
  nextTick(() => {
    setupPlayer();
  });
});

onBeforeUnmount(() => {
  try {
    if (player.value) {
      player.value.destroy();
    }
  } catch (ex: unknown) {
    console.error(ex);
  }
});
</script>

<template>
  <div
    v-if="youtubeEmbedId"
    ref="youtubeVideoContainer"
    class="yt-video__content"
    data-plyr-provider="youtube"
    :data-plyr-embed-id="youtubeEmbedId"
  />
</template>

<style lang="scss">
.yt-video {
  &__content {
    @apply w-full pt-[57%] relative;
  }

  &__element {
    @apply absolute top-0 left-0 w-full h-full m-0;
  }

  .plyr {
    &__control {
      @apply [&>svg]:m-auto;
    }
  }
}
</style>
